// src/components/public_utilities/pu/puMapStyles.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities map colours and legends
// --- UPDATE (Public Utilities v3 — PU-only map) — NEW FILE ---------------------------------------
// One place that decides how a Public Utilities asset is coloured on the GIS map, so the map
// layer, the legend and the toolbar always agree.
//   colorBy: 'type' | 'status' | 'due' (next inspection) | 'manager'
//   puColorFor(feature, colorBy, managerIndex) → hex
//   puLegend(layer, colorBy) → [{ key, label, color, count }]
// Colours are readable on both the dark CARTO basemap and light themes.
// --- END UPDATE ------------------------------------------------------------------------------

export const PU_COLOR_BY = Object.freeze([
    { value: 'type', label: 'Asset type' },
    { value: 'status', label: 'Status' },
    { value: 'due', label: 'Next inspection' },
    { value: 'manager', label: 'Managed by' },
]);

export const TYPE_COLORS = Object.freeze({
    drainage_well: '#38B2AC', catch_basin: '#4299E1', culvert: '#0BC5EA', manhole: '#81E6D9',
    building: '#ED8936', sidewalk: '#A0AEC0', bridge: '#D69E2E',
    street_light: '#F6E05E', traffic_signal: '#F56565', pole: '#B7791F', generator: '#DD6B20',
    pump: '#9F7AEA', lift_station: '#805AD5', valve: '#667EEA', hydrant: '#E53E3E', water_main: '#3182CE', meter: '#63B3ED', tank: '#2B6CB0', well: '#319795',
    sensor: '#48BB78', other: '#CBD5E0',
});

export const STATUS_COLORS = Object.freeze({
    operational: '#48BB78', planned: '#63B3ED', under_maintenance: '#4299E1', maintenance_required: '#ECC94B',
    damaged: '#ED8936', failed: '#E53E3E', closed: '#A0AEC0', decommissioned: '#718096', unknown: '#CBD5E0',
});

export const DUE_COLORS = Object.freeze({
    overdue: '#E53E3E', due_this_week: '#ED8936', due_soon: '#ECC94B', scheduled: '#48BB78', unscheduled: '#A0AEC0',
});
export const DUE_LABELS = Object.freeze({
    overdue: 'Overdue', due_this_week: 'Due this week', due_soon: 'Due in 30 days', scheduled: 'Scheduled later', unscheduled: 'Not scheduled',
});

const MANAGER_PALETTE = ['#38B2AC', '#ED64A6', '#9F7AEA', '#F6AD55', '#4FD1C5', '#F56565', '#68D391', '#63B3ED', '#D69E2E', '#B794F4', '#FC8181', '#90CDF4'];
const UNASSIGNED = '#A0AEC0';

const humanize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** manager id → colour, stable for the list order the server sends */
export function managerColorIndex(managers = []) {
    const m = new Map();
    managers.forEach((x, i) => m.set(String(x.id), MANAGER_PALETTE[i % MANAGER_PALETTE.length]));
    return m;
}

export function puColorFor(f, colorBy = 'type', managerColors) {
    if (!f) return UNASSIGNED;
    switch (colorBy) {
        case 'status': return STATUS_COLORS[f.status] || STATUS_COLORS.unknown;
        case 'due': return DUE_COLORS[f.due] || DUE_COLORS.unscheduled;
        case 'manager': return f.manager_id == null ? UNASSIGNED : (managerColors?.get(String(f.manager_id)) || UNASSIGNED);
        default: return TYPE_COLORS[f.type] || TYPE_COLORS.other;
    }
}

/** Is this asset worth flagging on a cluster (red ring)? Only from real data. */
export const needsAttention = (f) => f && (f.due === 'overdue' || f.status === 'failed' || f.status === 'damaged');

export function puLegend(layer, colorBy = 'type') {
    if (!layer) return [];
    const L = layer.legend || {};
    if (colorBy === 'status') {
        return Object.entries(L.status || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => ({ key: k, label: humanize(k), color: STATUS_COLORS[k] || STATUS_COLORS.unknown, count: n }));
    }
    if (colorBy === 'due') {
        return Object.keys(DUE_COLORS).filter((k) => (L.due || {})[k]).map((k) => ({ key: k, label: DUE_LABELS[k], color: DUE_COLORS[k], count: L.due[k] }));
    }
    if (colorBy === 'manager') {
        const colors = managerColorIndex(layer.managers);
        const rows = (layer.managers || []).filter((m) => m.assets > 0).map((m) => ({ key: String(m.id), label: m.name, color: colors.get(String(m.id)), count: m.assets }));
        if (L.unassigned_manager) rows.push({ key: 'none', label: 'Not assigned', color: UNASSIGNED, count: L.unassigned_manager });
        return rows.sort((a, b) => b.count - a.count);
    }
    return (L.types || []).map((t) => ({ key: t.key, label: t.label || humanize(t.key), color: TYPE_COLORS[t.key] || TYPE_COLORS.other, count: t.count }));
}

export const PU_ZONE_STYLE = Object.freeze({ color: '#B794F4', weight: 1.5, fillColor: '#B794F4', fillOpacity: 0.06, dashArray: '4, 4' });
export const ZONE_COLOR_HEX = Object.freeze({ red: '#E53E3E', orange: '#DD6B20', yellow: '#D69E2E', green: '#38A169', blue: '#3182CE', purple: '#805AD5', teal: '#319795', cyan: '#00B5D8' });
